import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation, Building2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HQ_COORDS = [40.9206293, 14.1900256];
const STRUCTURE_COORDS = [
  [40.968012, 14.255629],
  [40.9636813, 14.2704384],
  [41.5118596, 13.7732324],
  [40.8588654, 14.5173121],
];

const makeIcon = (hq) =>
  L.divIcon({
    className: "",
    html: `<div class="filia-marker${hq ? " filia-marker-hq" : ""}"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -14],
  });

const directionsUrl = ([lat, lng]) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

export const MapSection = () => {
  const { t } = useLanguage();

  const locations = [
    { name: t.map.hqLabel, address: t.map.hqAddress, coords: HQ_COORDS, hq: true },
    ...t.structures.items.map((s, i) => ({
      name: s.name,
      address: s.address,
      coords: STRUCTURE_COORDS[i],
      hq: false,
    })),
  ];

  return (
    <section id="sedi" data-testid="map-section" className="py-24 lg:py-32 bg-creamdark border-t border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-terra mb-6"
        >
          {t.map.eyebrow}
        </motion.p>
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-6 font-serif font-medium tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            {t.map.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 lg:col-start-8 text-base md:text-lg leading-relaxed text-inksoft self-end"
          >
            {t.map.sub}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl overflow-hidden border border-line shadow-[0_24px_70px_rgba(45,50,43,0.12)]"
        >
          <MapContainer
            data-testid="locations-map"
            center={[40.94, 14.12]}
            zoom={8}
            scrollWheelZoom={false}
            className="h-[420px] lg:h-[540px] w-full"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locations.map((loc, i) => (
              <Marker key={i} position={loc.coords} icon={makeIcon(loc.hq)}>
                <Popup>
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: 2 }}>{loc.name}</p>
                    <p style={{ margin: 0, color: "#5C635A" }}>{loc.address}</p>
                    <a
                      data-testid={`map-directions-${i}`}
                      href={directionsUrl(loc.coords)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#C86A51", fontWeight: 600, display: "inline-block", marginTop: 6 }}
                    >
                      {t.map.directions} →
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </motion.div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {locations.map((loc, i) => (
            <motion.a
              key={i}
              data-testid={`location-card-${i}`}
              href={directionsUrl(loc.coords)}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="group bg-white border border-line rounded-2xl p-5 flex items-start gap-4 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(200,106,81,0.10)] transition-[transform,box-shadow] duration-300"
            >
              <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${loc.hq ? "bg-ink/10" : "bg-terra/10"}`}>
                <Building2 size={17} strokeWidth={1.5} className={loc.hq ? "text-ink" : "text-terra"} />
              </span>
              <span className="flex-1">
                <span className="block font-serif text-lg font-medium text-ink">{loc.name}</span>
                <span className="block text-sm text-inksoft mt-0.5">{loc.address}</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-terra mt-2">
                  <Navigation size={12} strokeWidth={2} />
                  {t.map.directions}
                </span>
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
